import "./accordion.css";

import React, { useCallback, useEffect, useState } from "react";

import { cybervolunteerApi } from "../../../api/cybervolunteerApi";
import accordionChevronIcon from "../../../assets/img/icons/accordionChevronIcon.svg";
import accordionTaskDoneCheckIcon from "../../../assets/img/icons/accordionTaskDoneCheckIcon.svg";
import checkbox_on_check_icon from "../../../assets/img/icons/checkbox_on_check.svg";
import type { SubtaskDataType } from "../PersonalPageTypes";

export type AccordionProps = {
  subtask_id: SubtaskDataType["subtask_id"];
  title: string;
  desc: string;
  questions: SubtaskDataType["questions"];
  status: string;
  content: string;
  get_subtask_data: () => void;
  savedAnswers?: SubtaskDataType["saved_answers"];
  is_hero?: boolean;
};

type ExportedField = {
  id: number;
  type: string;
  content: string | number;
};

type StoredAnswer = { type: string; content: string | number };

function setFromCheckboxSaved(saved: string | undefined): Set<string> {
  const out = new Set<string>();
  if (saved == null || saved === "") return out;
  for (const token of String(saved).split(",")) {
    const t = token.trim();
    if (t) out.add(t);
  }
  return out;
}

type TextInputProps = {
  title: string;
  name: string;
  id: number;
  childToParent: (data: ExportedField) => void;
  disabled: boolean;
  initialSaved: string | undefined;
};

const TextInput: React.FC<TextInputProps> = ({
  title,
  name,
  id,
  childToParent,
  disabled,
  initialSaved,
}) => {
  const [returnableText, setReturnableText] = useState(() =>
    initialSaved != null ? String(initialSaved) : ""
  );

  useEffect(() => {
    setReturnableText(initialSaved != null ? String(initialSaved) : "");
  }, [initialSaved, id]);

  const export_data = useCallback(() => {
    return {
      id: id,
      type: "text",
      content: returnableText,
    };
  }, [id, returnableText]);

  useEffect(() => {
    childToParent(export_data());
  }, [childToParent, export_data]);

  return (
    <div className="flex flex-col lg:w-[740px] pt-3">
      <label className="text-md fw-700 mb-3 mr-10" htmlFor={String(id) + ":" + String(name)}>
        {title}:{" "}
      </label>
      <textarea
        disabled={disabled}
        value={returnableText}
        onChange={(e) => {
          setReturnableText(e.target.value);
        }} // ...force the input's value to match the state variable...
        className="h-40 border border-gray-300 p-3 align-bottom my-1"
        id={String(id) + ":" + String(name)}
      />
    </div>
  );
};

type QuestionOption = { id: number; text: string };

type RadioInputProps = {
  title: string;
  options: QuestionOption[] | null | undefined;
  id: number;
  childToParent: (data: ExportedField) => void;
  disabled: boolean;
  initialSaved: string | undefined;
};

const RadioInput: React.FC<RadioInputProps> = ({
  title,
  options,
  id,
  childToParent,
  disabled,
  initialSaved,
}) => {
  const optionList = Array.isArray(options) ? options : [];
  const [selectebleOption, setSelectebleOption] = useState<string | number>(() =>
    initialSaved != null && String(initialSaved).includes(":") ? String(initialSaved) : 0
  );

  useEffect(() => {
    setSelectebleOption(
      initialSaved != null && String(initialSaved).includes(":") ? String(initialSaved) : 0
    );
  }, [initialSaved, id]);

  const export_data = useCallback(() => {
    return {
      id: id,
      type: "radio",
      content: selectebleOption,
    };
  }, [id, selectebleOption]);

  useEffect(() => {
    childToParent(export_data());
  }, [export_data, childToParent]);

  return (
    <div className="flex flex-row">
      <fieldset className="flex flex-row" disabled={disabled}>
        <p className="text-md fw-700 mb-3 mr-10 pt-3">{title}: </p>
        {optionList.map((option, option_id) => {
          return (
            <div key={option_id} className="mr-3 pt-3">
              <input
                className="text"
                type="radio"
                onChange={() => {
                  setSelectebleOption(String(id) + ":" + String(option.id));
                }}
                id={String(id) + "_" + String(option.id)}
                checked={selectebleOption === String(id) + ":" + String(option.id)}
                name={String(id) + ":" + String(option.id)}
              />
              <label className="pl-2" htmlFor={String(id) + "_" + String(option.id)}>
                {option.text}
              </label>
            </div>
          );
        })}
      </fieldset>
    </div>
  );
};

type CheckboxInputProps = {
  title: string;
  options: QuestionOption[] | null | undefined;
  id: number;
  childToParent: (data: ExportedField) => void;
  disabled: boolean;
  initialSaved: string | undefined;
};

const CheckboxInput: React.FC<CheckboxInputProps> = ({
  title,
  options,
  id,
  childToParent,
  disabled,
  initialSaved,
}) => {
  const optionList = Array.isArray(options) ? options : [];
  const [selected, setSelected] = useState(() => setFromCheckboxSaved(initialSaved));

  useEffect(() => {
    setSelected(setFromCheckboxSaved(initialSaved));
  }, [initialSaved, id]);

  const toggle = (optionId: number) => {
    /* istanbul ignore next -- disabled fieldset inputs do not emit change events */
    if (disabled) return;
    const key = String(id) + ":" + String(optionId);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const export_data = useCallback(() => {
    const keys = Array.from(selected).sort();
    return {
      id: id,
      type: "check_box",
      content: keys.join(","),
    };
  }, [id, selected]);

  useEffect(() => {
    childToParent(export_data());
  }, [export_data, childToParent]);

  return (
    <div className="flex flex-col lg:w-[740px] pt-1">
      <fieldset className="flex flex-col" disabled={disabled}>
        <p className="text-md fw-700 mb-3 mr-10 pt-3">{title}: </p>
        {optionList.map((option) => {
          const key = String(id) + ":" + String(option.id);
          return (
            <div key={option.id} className="mr-3 pt-1 flex items-center">
              <input
                className="text"
                type="checkbox"
                id={String(id) + "_ch_" + String(option.id)}
                checked={selected.has(key)}
                onChange={() => toggle(option.id)}
              />
              <label className="pl-2" htmlFor={String(id) + "_ch_" + String(option.id)}>
                {option.text}
              </label>
            </div>
          );
        })}
      </fieldset>
    </div>
  );
};

const Accordion: React.FC<AccordionProps> = ({
  subtask_id,
  title,
  desc,
  questions,
  status,
  content,
  get_subtask_data,
  savedAnswers = {},
  is_hero = false,
}) => {
  /** Non-hero: false = collapsed; hero: always treated as expanded in layout. */
  const [isExpanded, setIsExpanded] = useState(Boolean(is_hero));
  const [returnedAnswers] = useState<Record<string, StoredAnswer>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [innerStatus, setInnerStatus] = useState(status);

  const childToParent = (data: ExportedField) => {
    returnedAnswers[String(data.id)] = {
      type: data.type,
      content: data.content,
    };
  };

  const handleItemClick = () => {
    /* istanbul ignore if -- hero tasks use a div header; this handler is only wired for non-hero */
    if (is_hero) return;
    setIsExpanded((v) => !v);
  };

  useEffect(() => {
    setInnerStatus(status);
  }, [status]);

  const send_subtask = useCallback(async () => {
    /* istanbul ignore next -- submit button is disabled while isSubmitting */
    if (isSubmitting) return;

    const answers = Object.keys(returnedAnswers).map((kee) => ({
      id: kee,
      content: returnedAnswers[kee].content,
      type: returnedAnswers[kee].type,
    }));

    setIsSubmitting(true);
    setSubmitError("");

    try {
      await cybervolunteerApi.sendSubtaskAnswers({ subtask_id, answers });

      setInnerStatus("close");
      get_subtask_data();
    } catch (error) {
      const err = error && typeof error === "object" && "response" in error ? error : null;
      const resp =
        err && typeof (err as { response?: unknown }).response === "object"
          ? (err as { response: { status?: number; data?: unknown } }).response
          : null;
      if (resp) {
        let message = `Ошибка отправки (${resp.status})`;
        const payload = resp.data as { message?: string };
        if (payload && payload.message) message = payload.message;
        setSubmitError(message);
        return;
      }
      console.error("Error sending task answers:", error);
      setSubmitError("Сеть недоступна. Попробуй ещё раз.");
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, returnedAnswers, subtask_id, get_subtask_data]);

  const headerContent = (
    <>
      {!is_hero ? (
        <span className="accordion-arrow" aria-hidden>
          <img
            src={accordionChevronIcon}
            alt=""
            className={
              isExpanded
                ? "accordion-arrow-icon accordion-arrow-icon--expanded"
                : "accordion-arrow-icon"
            }
          />
        </span>
      ) : null}
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-1-25">
          {innerStatus === "close" ? (
            <span
              className="accordion-title-done-icon"
              title="Задача выполнена"
              aria-label="Задача выполнена"
            >
              <img
                src={accordionTaskDoneCheckIcon}
                alt=""
                aria-hidden
                className="accordion-title-done-icon-svg"
              />
            </span>
          ) : innerStatus === "on_check" ? (
            <img
              src={checkbox_on_check_icon}
              className="mt-1 h-8 w-8 shrink-0"
              alt="Задача проверяется"
            />
          ) : innerStatus !== "open" ? (
            <span className="mt-2 shrink-0" aria-hidden>
              ?
            </span>
          ) : null}
          <div className="min-w-0 flex-1">
            <h2 className="accordion-task-title font-bold text-[32px] leading-[1.2] mb-3">
              {title}
            </h2>
            <p className="text-lg mb-3">{desc}</p>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div
      className={
        innerStatus === "open"
          ? is_hero
            ? "bg-white mb-7"
            : "bg-white"
          : "bg-neutral-100 container mx-auto overflow-hidden text-clip"
      }
    >
      {is_hero ? (
        <div test-id="hero-task-header" className="text-left pt-4 mx-5 flex items-start">
          {headerContent}
        </div>
      ) : (
        <button
          type="button"
          test-id="accordion-button"
          className="text-left pt-4 mx-5 flex items-start"
          onClick={handleItemClick}
          aria-expanded={isExpanded}
        >
          {headerContent}
        </button>
      )}
      <div
        test-id="accordion-content"
        className={
          is_hero
            ? "text-left pt-4 mx-6"
            : [
                "accordion-panel-root mx-6 md:mx-20",
                isExpanded ? "accordion-panel-root--expanded" : "",
              ]
                .filter(Boolean)
                .join(" ")
        }
      >
        <div className={is_hero ? undefined : "accordion-panel-inner"}>
          <div
            className="accordion-text ck-content overflow-hidden text-clip text-black lg:w-[740px]"
            dangerouslySetInnerHTML={{ __html: content }}
          />
          {is_hero ? (
            <div className="" />
          ) : (
            <>
              <input type="hidden" name="subtask_id" value={subtask_id} />
              {questions.map(function (question) {
                const locked = innerStatus !== "open";
                const initialSaved = savedAnswers[String(question.id)];
                switch (question.question_type) {
                  case "radio":
                    return (
                      <RadioInput
                        key={question.id}
                        childToParent={childToParent}
                        title={question.content.title}
                        options={question.content.options ?? null}
                        id={question.id}
                        disabled={locked}
                        initialSaved={initialSaved}
                      />
                    );
                  case "check_box":
                    return (
                      <CheckboxInput
                        key={question.id}
                        childToParent={childToParent}
                        title={question.content.title}
                        options={question.content.options ?? null}
                        id={question.id}
                        disabled={locked}
                        initialSaved={initialSaved}
                      />
                    );
                  case "textarea":
                    return (
                      <TextInput
                        key={question.id}
                        childToParent={childToParent}
                        title={question.content.title}
                        name={question.content.name ?? ""}
                        id={question.id}
                        disabled={locked}
                        initialSaved={initialSaved}
                      />
                    );
                  default:
                    return <input key={question.id} type="hidden" />;
                }
              })}
              <button
                onClick={send_subtask}
                test-id="send-subtask-button"
                disabled={innerStatus !== "open" || isSubmitting}
                className="mb-2 mt-3 block bg-green color-white step-button text-center justify-center w-40 h-10 disabled:opacity-50"
              >
                {isSubmitting ? "Отправка..." : "Отправить"}
              </button>
              {submitError ? (
                <p className="mb-7 text-red-600 text-sm">{submitError}</p>
              ) : (
                <div className="mb-7" />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Accordion;
